import React, { useEffect, useMemo, useState } from 'react';
import { marked } from 'marked';
import {
  resolveDocumentDisplayTitle,
  splitMarkdownFrontmatter
} from '../utils/markdownFrontmatter';

function MetadataChip({ label, value }) {
  if (!value || (Array.isArray(value) && value.length === 0)) return null;
  const displayValue = Array.isArray(value) ? value.join(', ') : value;
  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
      <span className="mr-1 text-slate-500">{label}:</span>
      {displayValue}
    </span>
  );
}

const DocsContent = ({ content, currentFile, isLoading, onFileSelect, redirectInfo, metadata }) => {
  const [renderedContent, setRenderedContent] = useState('');

  const { body } = useMemo(() => splitMarkdownFrontmatter(content), [content]);
  const pageTitle = resolveDocumentDisplayTitle({ metadata, content, currentFile });

  // Configure marked options
  useEffect(() => {
    marked.setOptions({
      breaks: true,
      gfm: true,
      highlight: function(code, lang) {
        return code;
      }
    });
  }, []);

  useEffect(() => {
    if (body) {
      const html = marked.parse(body);
      setRenderedContent(html);
    } else {
      setRenderedContent('');
    }
  }, [body]);

  // Handle link clicks to load other documentation files
  useEffect(() => {
    const handleLinkClick = (e) => {
      const link = e.target.closest('a');
      if (link && link.href.includes('/docs/')) {
        e.preventDefault();
        const path = link.getAttribute('href');
        if (path && path.startsWith('/docs/')) {
          onFileSelect(path);
        }
      }
    };

    const contentDiv = document.getElementById('docs-content');
    if (contentDiv) {
      contentDiv.addEventListener('click', handleLinkClick);
      return () => contentDiv.removeEventListener('click', handleLinkClick);
    }
  }, [onFileSelect]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Breadcrumb */}
        {currentFile && (
          <nav className="flex mb-6" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <div className="flex items-center">
                  <svg className="flex-shrink-0 h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                  </svg>
                  <span className="sr-only">Home</span>
                </div>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="flex-shrink-0 h-5 w-5 text-gray-300" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="ml-4 text-sm font-medium text-gray-500">
                    {pageTitle}
                  </span>
                </div>
              </li>
            </ol>
          </nav>
        )}

        {redirectInfo && (
          <div className="mb-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p className="font-medium">Dokumentet er flyttet</p>
            <p className="mt-1">
              <code className="rounded bg-amber-100 px-1 py-0.5">{redirectInfo.from}</code>
              <span className="mx-2">→</span>
              <code className="rounded bg-amber-100 px-1 py-0.5">{redirectInfo.to}</code>
            </p>
          </div>
        )}

        {metadata && (
          <div className="mb-6 rounded-md border border-slate-200 bg-white px-4 py-3">
            <div className="flex flex-wrap gap-2">
              <MetadataChip label="source" value={metadata.source} />
              <MetadataChip label="type" value={metadata.type} />
              <MetadataChip label="status" value={metadata.status} />
              <MetadataChip label="module" value={metadata.module} />
              <MetadataChip label="owners" value={metadata.owners} />
              <MetadataChip label="tags" value={metadata.tags} />
              {metadata.visibility === 'internal' && (
                <MetadataChip label="visibility" value="internal" />
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div
          id="docs-content"
          className="prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: renderedContent }}
        />
      </div>
    </div>
  );
};

export default DocsContent;
